import { useQuery } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature, SaleClient } from 'src/WebApiController';

export const SALES_GROUP_QUERY_KEY = 'getSalesGroup';

/**
 * Fetches the SalesGroup payload (header + member sales + group-level financial
 * aggregates) for the read-only Sales Group modal (POS-8632).
 *
 * Gated by Feature.SalesGroupView: the query is only enabled when the feature is
 * on, a salesGroupId is provided, and an active account is present. Returns null
 * when disabled so callers can render a neutral/empty state.
 */
export function useGetSalesGroup(salesGroupId?: string | null) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasSalesGroupViewFeature = useUserHasFeature(Feature.SalesGroupView);

  const shouldQuery =
    hasSalesGroupViewFeature &&
    salesGroupId != null &&
    !!activeAccountWebClientConfig?.activeAccountId;

  return useQuery({
    queryKey: [SALES_GROUP_QUERY_KEY, salesGroupId],
    async queryFn() {
      if (!shouldQuery || !salesGroupId) {
        return null;
      }
      try {
        return await new SaleClient(activeAccountWebClientConfig).getSalesGroup(
          salesGroupId
        );
      } catch (err: unknown) {
        trackError('SaleClient.getSalesGroup', err as ErrorTypes, {
          salesGroupId,
        });
        throw err;
      }
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });
}
